import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { MembershipBenefits } from "@/components/MembershipBenefits";
import CaptchaCheck from "@/components/checkout/CaptchaCheck";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import { money } from "@/lib/format";

export default function Join() {
  const { joinDiscovery } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState(params.get("email") || "");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [cap, setCap] = useState({});
  const [capKey, setCapKey] = useState(0);
  const [consent, setConsent] = useState(true);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { data: plans = [] } = useQuery({ queryKey: ["join-plans"], queryFn: () => api.get("/memberships").then((r) => r.data) });
  const priceFor = (key, fallback) => {
    const p = plans.find((m) => m.key === key);
    if (!p) return fallback;
    if (!p.price || p.interval === "free") return "Free";
    return `${money(Number(p.price))}/${p.interval === "year" ? "year" : "month"}`;
  };
  const fromGift = !!params.get("email");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await joinDiscovery({ email, password, first_name: firstName, last_name: lastName, phone, consent, accepted_terms: acceptedTerms, origin: window.location.origin, captcha_token: cap.token, captcha_answer: cap.answer, website: cap.website });
      toast.success(fromGift ? "Welcome! Your gifted sessions are ready to book." : "Welcome — you're now a Discovery Member.");
      nav(params.get("next") || "/dashboard");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(formatApiError(d?.message ? d.message : d) || err.message);
      setCapKey((k) => k + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2">
      <Seo title="Join — Free Discovery Membership" description="Join Enhancing Your World free as a Discovery Member to book offerings, follow enhancers, attend events and stay connected to the community." />
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1518241353330-0f7941c2d9b5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Community" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#144F75]/70" />
        <div className="relative p-14 h-full flex flex-col justify-end">
          <blockquote className="font-serif text-3xl text-white leading-tight">You do not have to know where to begin.</blockquote>
          <p className="text-white/80 mt-4">Join as a Discovery Member and stay connected to the living ecosystem.</p>
        </div>
      </div>
      <div className="grid place-items-center px-6 py-16">
        <div className="w-full max-w-md" data-testid="join-page">
          <img src="https://customer-assets-wrfwihn1.emergentagent.net/job_eyw-admin-suite/artifacts/ly5amm9a_EnhancingYourWorld2-02_compass57x57.png" alt="Enhancing Your World" className="h-12 w-12 rounded-full mb-6 object-contain" />
          <p className="eyw-label mb-1">Discovery Membership · Free</p>
          <h1 className="font-serif text-3xl text-[#1A242B] mb-6">Begin as a Discovery Member.</h1>
          <MembershipBenefits plan="discovery" className="mb-6" />
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <input required placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="join-firstname" className="eyw-input w-full" />
              <input required placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="join-lastname" className="eyw-input w-full" />
            </div>
            <input required type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="join-email" className="eyw-input w-full" />
            <input placeholder="Phone (optional)" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="join-phone" className="eyw-input w-full" />
            <input required type="password" minLength={12} autoComplete="new-password" placeholder="Create a password (12+ characters, letters & numbers)" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="join-password" className="eyw-input w-full" />
            <CaptchaCheck value={cap} onChange={setCap} refreshKey={capKey} testid="join-captcha" />
            <label className="flex items-start gap-2 text-xs text-[#4A5D6B]">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
              I agree to receive community updates and understand I can unsubscribe at any time.
            </label>
            <label className="flex items-start gap-2 text-xs text-[#4A5D6B] cursor-pointer">
              <input required type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} data-testid="join-terms-checkbox" className="mt-0.5 accent-[#144F75]" />
              <span>I agree to the <Link to="/terms" target="_blank" className="text-[#144F75] underline">Terms of Service</Link> and <Link to="/privacy" target="_blank" className="text-[#144F75] underline">Privacy Policy</Link>.</span>
            </label>
            {error && <p className="text-sm text-red-600" data-testid="join-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="join-submit" className="eyw-btn w-full disabled:opacity-60">
              {loading && <Loader2 className="animate-spin" size={18} />} Join Free
            </button>
          </form>
          <p className="text-sm text-[#4A5D6B] mt-6">
            Want deeper access? <Link to="/register?role=community" className="text-[#144F75] font-medium">Join Community</Link> ·
            Already a member? <Link to="/login" className="text-[#144F75] font-medium">Login</Link>
          </p>

          <div className="mt-10 pt-8 border-t border-[#E5E0D8]" data-testid="join-hub">
            <p className="eyw-label mb-1">Ways to join</p>
            <h2 className="font-serif text-2xl text-[#1A242B] mb-4">Choose how you enhance your world</h2>
            <div className="space-y-3">
              {[
                { to: "/join?level=discovery#top", title: "Discovery", price: priceFor("discovery", "Free"), desc: "Your gentle first step. Stay connected to the living ecosystem, explore offerings and receive guidance at your own pace — no commitment, just belonging." },
                { to: "/memberships", title: "Community Member", price: priceFor("community", "Monthly membership"), desc: "Make wellbeing a rhythm, not an afterthought. Unlock member pricing, priority booking and a supportive community so caring for yourself becomes second nature." },
                { to: "/enhancer/apply", title: "Enhancer", price: priceFor("enhancer", "Recurring · apply"), desc: "Turn your gifts into a thriving practice. Reach aligned clients, grow steady recurring income and help others transform — while your work ripples out across the ecosystem." },
                { to: "/partner", title: "Center", price: priceFor("center", "Recurring · apply"), desc: "Bring Enhancing Your World to your town. Full operations, booking and payments support so your Center becomes a beloved hub for healing and connection." },
                { to: "/vendor/apply", title: "Vendor", price: priceFor("vendor", "Recurring · apply"), desc: "Share products people can trust. Sell to a values-aligned marketplace of members and Centers who care about what they bring into their lives." },
              ].map((o) => (
                <Link key={o.title} to={o.to} data-testid={`join-level-${o.title.split(" ")[0].toLowerCase()}`} className="eyw-card p-4 block hover:-translate-y-0.5 transition-transform">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="font-semibold text-[#1A242B]">{o.title}</p>
                    <span className="text-xs text-[#144F75] font-medium shrink-0">{o.price}</span>
                  </div>
                  <p className="text-sm text-[#4A5D6B] mt-0.5">{o.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
